import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/axios.js';

export function useLeads(filters = {}) {
  return useQuery({
    queryKey: ['leads', filters],
    queryFn: () => api.get('/leads', { params: filters }).then((r) => r.data),
  });
}

// El nombre del archivo lo decide el backend; esto es solo el plan B si el
// header no viaja (por ejemplo si algún proxy lo filtra).
function nombreDesdeHeader(headers) {
  const match = /filename="?([^"]+)"?/.exec(headers?.['content-disposition'] ?? '');
  return match?.[1] ?? 'leads.xlsx';
}

function dispararDescarga(blob, nombre) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Con responseType 'blob' los errores del backend también llegan como Blob,
// así que hay que desempaquetarlos para poder mostrar el mensaje real.
async function mensajeDeErrorBlob(error) {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const { error: msg } = JSON.parse(await data.text());
      if (msg) return msg;
    } catch { /* no era JSON, cae al genérico */ }
  }
  return 'No se pudo generar el Excel. Probá de nuevo.';
}

export function useExportarLeads() {
  return useMutation({
    mutationFn: async (filters = {}) => {
      // `page` se descarta a propósito: la exportación no pagina, trae todo lo
      // que matchea los filtros actuales.
      const { page, ...sinPaginado } = filters;
      try {
        const res = await api.get('/leads/export', { params: sinPaginado, responseType: 'blob' });
        dispararDescarga(res.data, nombreDesdeHeader(res.headers));
      } catch (error) {
        throw new Error(await mensajeDeErrorBlob(error));
      }
    },
  });
}

export function useLead(id) {
  return useQuery({
    queryKey: ['lead', id],
    queryFn: () => api.get(`/leads/${id}`).then((r) => r.data),
    enabled: !!id,
  });
}

export function useCreateLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => api.post('/leads', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leads'] }),
  });
}

export function useUpdateLead(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => api.patch(`/leads/${id}`, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leads'] });
      qc.invalidateQueries({ queryKey: ['lead', id] });
    },
  });
}

export function useReasignarLead(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vendedor_id) => api.patch(`/leads/${id}/reasignar`, { vendedor_id }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lead', id] }),
  });
}

export function useDeleteLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/leads/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leads'] }),
  });
}
